import CommonLayout, { CommonLayoutInstance } from '@/components/CommonLayout';
import { Card, Col, DatePicker, Empty, Flex, Input, Radio, Row, Tree } from 'antd';
import React, { forwardRef, memo, useImperativeHandle, useMemo, useRef, useState } from 'react';
import { SearchOutlined, DisconnectOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { getDeviceList } from '@/api/video';
import { WsFlvWithUrl } from '../components';
import { Dayjs } from 'dayjs';
import { v4 as uuidV4 } from 'uuid';
// import { useSystem } from '@/hooks/useUserInfo';

const VideoWsFlvItem = memo(
  ({
    span,
    // isPlayback,
    // times,
    onChose,
    isSelected,
    index,
    ...item
  }: {
    span: number;
    id: string;
    isSelected?: boolean;
    deviceSn?: string;
    channelId?: string;
    times?: number[];
    isPlayback?: boolean;
    onChose: (index: number) => void;
    index: number;
    deviceId?: string;
  }) => {
    // const { systemData } = useSystem();
    // const ipc_addr = systemData.ipc_addr;
    if (!item.deviceId || !item.channelId) {
      return (
        <Col span={span} key={`${item.id}_${index}`}>
          <Card
            className={`shadow ${isSelected ? 'active' : ''}`}
            classNames={{ body: 'shadow-body' }}
            onClick={() => {
              onChose(index);
            }}
          >
            <div style={{ width: 'auto', paddingBottom: '56.25%', position: 'relative' }}>
              <div style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0, textAlign: 'center', display: 'flex', justifyContent: 'center', alignContent: 'center' }}>
                <DisconnectOutlined style={{ fontSize: { 24: 80, 12: 60, 8: 50, 6: 40 }[span], color: '#ddd' }} />
              </div>
            </div>
          </Card>
        </Col>
      );
    }
    return (
      <Col span={span} key={`${item.id}_${index}`}>
        <Card
          className={`shadow ${isSelected ? 'active' : ''}`}
          classNames={{ body: 'shadow-body' }}
          onClick={() => {
            onChose(index);
          }}
        >
          <div style={{ width: 'auto', paddingBottom: '56.25%', position: 'relative' }}>
            <div style={{ position: 'absolute', width: '100%', height: '100%', top: 0, left: 0 }}>
              <WsFlvWithUrl id={`player-${item.channelId!.slice(-4)}-screen-${index}`} deviceId={item.deviceId!} channelId={item.channelId!}></WsFlvWithUrl>
            </div>
          </div>
        </Card>
      </Col>
    );
  },
);

const spans = { 24: 1, 12: 4, 8: 9, 6: 16 };
const baseData = Object.fromEntries(Object.entries(spans).map(([key, value]) => [key, new Array(value).fill(0).map(() => ({ id: uuidV4() }))]));
export const VideoScreenContent = forwardRef<
  { changeData: (item: { deviceId: string; deviceSn: string; channelId: string; name: string; id: string }) => void },
  {
    selectedData?: {
      deviceSn: string;
      channelId: string;
      name: string;
      id: string;
      deviceId: string;
    }[];
    isPlayback?: boolean;
  }
>(({ selectedData, isPlayback }, ref) => {
  const [span, setSpan] = useState<number>(24);
  const [times, setTimes] = useState<number[]>([]);
  const fullContainer = useRef<HTMLDivElement>(null);

  const showData = useMemo(() => {
    const minLength = { 24: 1, 12: 4, 8: 9, 6: 16 }[span] as number;
    const rowNum = 24 / span;
    const length = selectedData?.length || 0;
    const rest = length % rowNum;

    return length > minLength
      ? [...(selectedData || []), ...(rest ? new Array(rowNum - rest).fill(0).map(() => ({ id: uuidV4() })) : [])]
      : [...(selectedData || []), ...new Array(minLength - length).fill(0).map(() => ({ id: uuidV4() }))];
  }, [span, selectedData]);

  const [selectedIndex, setSelectedIndex] = useState<number | undefined>();

  const [screenData, setScreenData] = useState<{ id: string }[]>(baseData[24]);

  useImperativeHandle(ref, () => {
    return {
      changeData: (item: { deviceSn: string; channelId: string; name: string; id: string; deviceId: string }) => {
        if (typeof selectedIndex === 'number') {
          const list = [...screenData];
          list[selectedIndex] = item;
          setScreenData([...list]);
        } else {
          const list = [...screenData];
          list[0] = item;
          setSelectedIndex(0);
          setScreenData(list);
        }
      },
    };
  });

  return (
    <div className="">
      {!isPlayback && (
        <Flex justify="center">
          <Radio.Group
            options={[
              { label: '单屏', value: 24 },
              { label: '四分屏', value: 12 },
              { label: '九分屏', value: 8 },
              { label: '十六分屏', value: 6 },
              { label: '全屏', value: 100 },
            ]}
            optionType="button"
            value={span}
            onChange={(e) => {
              if (e.target.value === 100) {
                fullContainer.current?.requestFullscreen();
                return;
              }
              setScreenData(baseData[e.target.value]);
              setSpan(e.target.value);
            }}
          ></Radio.Group>
        </Flex>
      )}
      {isPlayback && (
        <Flex style={{ margin: '0 24px' }}>
          <DatePicker.RangePicker
            placeholder={['开始时间', '结束时间']}
            onChange={(e) => {
              if (e?.length) setTimes(e.map((e) => (e as Dayjs).unix()));
              else setTimes([]);
            }}
          ></DatePicker.RangePicker>
        </Flex>
      )}
      <div style={{ height: 'calc(100vh - 200px)', overflowY: 'auto', marginTop: '8px', padding: '0 24px ' }}>
        <div ref={fullContainer}>
          <Row gutter={[16, 16]}>
            {isPlayback &&
              showData?.map((item, index) => (
                <VideoWsFlvItem
                  key={`${item.id}_${index}`}
                  onChose={(index) => {
                    setSelectedIndex(index);
                  }}
                  index={index}
                  {...{ span, times, isPlayback, isSelected: index === selectedIndex, ...item }}
                ></VideoWsFlvItem>
              ))}
            {!isPlayback &&
              screenData?.map((item, index) => (
                <VideoWsFlvItem
                  key={`${item.id}_${index}`}
                  onChose={(index) => {
                    setSelectedIndex(index);
                  }}
                  index={index}
                  {...{ span, times, isPlayback, isSelected: index === selectedIndex, ...item }}
                ></VideoWsFlvItem>
              ))}
          </Row>
        </div>
      </div>
    </div>
  );
});

export const VideoTreeWrapper = ({ Content, isPlayback }: { Content: typeof VideoScreenContent; isPlayback?: boolean }) => {
  const CommonLayoutRef = useRef<CommonLayoutInstance<{ params: {}; model: {}; drawerParams: {}; drawerModel: {} }>>(null);
  const [list, setList] = useState<{ name: string; id: string; channels?: { name: string; id: string }[] }[]>([]);
  // const [treeData, setTreeData] = useState<{ name: string; id: string; channels?: { name: string; id: string }[] }[]>([]);
  const [value, setValue] = useState<string>();
  const treeData = useMemo(() => {
    if (!value) return list;
    return list.reduce<{ name: string; id: string; channels?: { name: string; id: string }[] }[]>((pre, item) => {
      const channels = item.channels?.filter((channel) => channel.name?.includes(value));
      if (channels?.length || item.name.includes(value)) {
        pre.push({ ...item, channels });
      }

      return pre;
    }, []);
  }, [list, value]);

  const [selectedKeys, setSelectedKeys] = useState<string[]>([]);
  const [selectedData, setSelectedData] = useState<{ deviceId: string; name: string; id: string; deviceSn: string; channelId: string }[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const ContentRef = useRef<{ changeData: (item: { deviceId: string; deviceSn: string; channelId: string; name: string; id: string }) => void }>(null);
  return (
    <CommonLayout
      ref={CommonLayoutRef}
      breadcrumb={{
        title: '录像回放',
        describe: '',
      }}
      isHideBreadcrumb
      ajaxJson={{
        get: {
          // @ts-ignore
          ajax: getDeviceList,
          params: { isAll: true },
          beforeFun: () => {
            setLoading(true);
          },
          finallyFun: () => {
            setLoading(false);
          },
          transform: (values) => {
            // let firstKey = '';
            const data =
              values?.list?.map((item: { id: string; channels: any[]; deviceSn: string; name: string }) => {
                const newItem = {
                  ...item,
                  disabled: true,
                  channels: item.channels?.map((channel) => ({ ...channel, deviceSn: item.deviceSn })),
                };
                return newItem;
              }) || [];

            setList(data);

            return {};
          },
        },
      }}
      mainType={'self'}
    >
      <Row gutter={16} style={{ height: 'calc(100vh - 110px)', overflow: 'hidden' }}>
        <Col span={8}>
          <Card style={{ height: '100%', overflow: 'hidden' }} loading={loading}>
            <Input
              placeholder="模糊搜索设备名称"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
              }}
              prefix={<SearchOutlined />}
            ></Input>
            <div style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', marginTop: '8px' }}>
              <Tree.DirectoryTree
                /** @ts-ignore */
                treeData={treeData}
                fieldNames={{ children: 'channels', title: 'name', key: 'id' }}
                blockNode
                selectedKeys={selectedKeys}
                defaultExpandAll
                onSelect={(selectedKeys, info) => {
                  console.log({ selectedKeys });
                  if (!selectedKeys?.length) {
                    if (!isPlayback) {
                      ContentRef.current?.changeData(selectedData[0] as { deviceId: string; deviceSn: string; channelId: string; name: string; id: string });
                    }
                    return;
                  }
                  const {
                    selectedNodes: [item],
                  } = info;
                  setSelectedKeys(selectedKeys as string[]);
                  // @ts-ignore
                  setSelectedData([item]);
                  if (!isPlayback) {
                    console.log(item);
                    ContentRef.current?.changeData(item as { deviceSn: string; deviceId: string; channelId: string; name: string; id: string });
                  }
                }}
                icon={(props) => {
                  // console.log({ props });
                  // @ts-ignore
                  return !props.channels?.length ? <PlayCircleOutlined /> : <></>;
                }}
              ></Tree.DirectoryTree>
              {!treeData?.length && <Empty></Empty>}
            </div>
          </Card>
        </Col>
        <Col span={16}>
          <Card style={{ height: '100%', overflow: 'hidden', padding: 0 }} styles={{ body: { padding: '24px 0 0', height: '100%' } }}>
            <Content ref={ContentRef} selectedData={selectedData} isPlayback={isPlayback}></Content>
          </Card>
        </Col>
      </Row>
    </CommonLayout>
  );
};

const VideoScreen = () => {
  return <VideoTreeWrapper Content={VideoScreenContent}></VideoTreeWrapper>;
};
export default VideoScreen;
